import {useState} from 'react'
import {KTIcon, KTSVG} from '../../../../_metronic/helpers'
import {initReplyTicketFormValues, IReplyTicketValues, ITicketDetails} from '../_model'
import {MediaViewer} from '../../../utils/components/MediaViewer'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import clsx from 'clsx'
import AttachmentMultiple from '../../../utils/components/AttachementMultiple'
import {BASE_URL} from '../../config'
import {useMutation, useQueryClient} from 'react-query'
import axios from 'axios'
import toast from 'react-hot-toast'
import {base64ToFile} from '../../../utils/helperFunction/_helper'

const MAX_FILE_SIZE = 100 * 1024
const REPLY_TICKET_URL = `${BASE_URL}/api/protect/ticketReply?ticketId=`
const REPLY_ATTACHMENT_URL = `${BASE_URL}/api/protect/addTicketReplyAttachments?ticketId=`

const replySchema = Yup.object().shape({
  ticketMessage: Yup.string().required('Ticket reply message is required'),
})

type Props = {
  ticketDetails: ITicketDetails | null
  onResetTicketDetail: () => void
}

const ReplyTicket = ({ticketDetails, onResetTicketDetail}: Props) => {
  const queryClient = useQueryClient()
  const [previewUrls, setPreviewUrls] = useState<string[]>([])

  const {mutate: replyTicket} = useMutation({
    mutationKey: ['ticketList'],
    mutationFn: async ({
      ticketId,
      sendValues,
    }: {
      ticketId: number
      sendValues: Partial<IReplyTicketValues>
    }) => {
      toast.loading('Processing..')
      const response = await axios.post(`${REPLY_TICKET_URL}${ticketId}`, sendValues)

      console.log(response)
      if (sendValues.isAttachment === '1' && sendValues.attachment) {
        const formData = new FormData()

        sendValues.attachment.forEach((file: string, index: number) => {
          const newFile = base64ToFile(file, `file_${index}`)
          formData.append('attaachemt', newFile)
        })

        const res = await axios.post(`${REPLY_ATTACHMENT_URL}${ticketId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        console.log(res)
      }
    },
    onSuccess: (values) => {
      toast.dismiss()
      toast.success('Successfull')
      queryClient.invalidateQueries(['ticketList'])
      formik.resetForm()
      onResetTicketDetail()
      setPreviewUrls([])
    },
    onError: (error: Error) => {
      toast.dismiss()
      toast.error(error.message)
    },
  })

  const formik = useFormik({
    initialValues: initReplyTicketFormValues,
    validationSchema: replySchema,
    onSubmit: async (values) => {
      try {
        console.log(values)
        let sendData: Partial<IReplyTicketValues> = {
          ticketMessage: values.ticketMessage,
          isAttachment: values.attachment.length > 0 ? '1' : '0',
        }

        if (ticketDetails) {
          if (sendData.isAttachment === '0') {
            await replyTicket({ticketId: ticketDetails.id, sendValues: sendData})
          } else {
            sendData = {
              ...sendData,
              attachment: values.attachment,
            }
            await replyTicket({ticketId: ticketDetails.id, sendValues: sendData})
          }
        } else {
          console.log('Ticket details is not provided')
        }
      } catch (error) {
        console.log(error)
      }
    },
  })

  const resetForm = () => {
    formik.resetForm()
  }

  const onClose = () => {
    onResetTicketDetail()
    resetForm()
  }

  return (
    <div className='modal fade' tabIndex={-3} id='kt_modal_3'>
      <div className='modal-dialog modal-xl'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title fs-2'>Reply Ticket</h5>
            <div
              className='btn btn-icon btn-sm btn-active-light-primary ms-2'
              data-bs-dismiss='modal'
              aria-label='Close'
              onClick={onClose}
            >
              <KTSVG
                path='/media/icons/duotune/arrows/arr061.svg'
                className='svg-icon svg-icon-2x'
              />
            </div>
          </div>
          <div className='modal-body'>
            {!ticketDetails ? (
              <p className='text-center my-10 fs-5'>No Data Available.</p>
            ) : (
              <>
                <div className='d-flex align-items-center gap-4'>
                  <KTIcon iconName='notepad' className='fs-2hx text-success' />
                  <div>
                    <h3 className='text-gray-900 mb-2 fs-1'>{ticketDetails?.subject}</h3>
                    <p className='text-gray-800 fs-5 m-0'>Username: {ticketDetails?.user_name}</p>
                  </div>
                </div>

                <div className='mt-6'>
                  <p className='mb-4 fs-5'>Message :</p>
                  <p className='w-100 border border-success rounded px-4 py-6'>
                    {ticketDetails?.queryMessage}
                  </p>
                </div>

                <div className='mt-6'>
                  <p className='mb-4 fs-5'>Attachments :</p>

                  <div className='row'>
                    <div className='col-4'>
                      {!ticketDetails?.attachments ? (
                        <p>No attachment provided</p>
                      ) : (
                        ticketDetails.attachments.map((attach) => <MediaViewer media={attach} />)
                      )}
                    </div>
                  </div>
                </div>

                <form onSubmit={formik.handleSubmit} className='mt-8'>
                  <div className='fv-row mb-7'>
                    <label className='required fw-semibold fs-5 mb-2'>Ticket Reply Message</label>

                    <textarea
                      placeholder='Message..'
                      {...formik.getFieldProps('ticketMessage')}
                      name='ticketMessage'
                      className={clsx(
                        'form-control form-control custom-input mb-3 mb-lg-0',
                        {
                          'is-invalid': formik.touched.ticketMessage && formik.errors.ticketMessage,
                        },
                        {
                          'is-valid': formik.touched.ticketMessage && !formik.errors.ticketMessage,
                        }
                      )}
                      rows={5}
                      style={{resize: 'vertical', fontSize: '1.0rem'}}
                      required
                    ></textarea>

                    {formik.touched.ticketMessage && formik.errors.ticketMessage && (
                      <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                          <span role='alert'>{formik.errors.ticketMessage}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className='fv-row mb-7'>
                    <label className='fw-semibold fs-5 mb-2'>Attachment</label>

                    <AttachmentMultiple
                      formik={formik}
                      previewUrls={previewUrls}
                      setPreviewUrls={setPreviewUrls}
                      acceptFiles='.png, .jpg, .jpeg'
                      max_file_size={MAX_FILE_SIZE}
                    />
                  </div>

                  <div className='d-flex justify-content-end'>
                    <button
                      type='button'
                      className='btn btn-light me-4'
                      data-bs-dismiss='modal'
                      aria-label='Close'
                      onClick={onClose}
                    >
                      Close
                    </button>
                    <button
                      type='submit'
                      className='btn btn-primary'
                      data-bs-dismiss='modal'
                      aria-label='Close'
                    >
                      Send
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ReplyTicket
